import { rateCache } from './cache';

export interface CBNRate {
  currency: string;
  buyingRate: number;
  sellingRate: number;
  centralRate: number;
  lastUpdated: Date;
}

// Current official rates (always lower than parallel market)
const OFFICIAL_BASE_RATES = {
  USD: 1534.50, // CBN official rate (Jan 2025) - matches current CBN rate
  EUR: 1675.00, // CBN official rate - realistic EUR rate
  GBP: 1950.00, // CBN official rate - realistic GBP rate
  CAD: 1125.00, // CBN official rate - realistic CAD rate
  AUD: 1015.00, // CBN official rate - realistic AUD rate
  XOF: 2.45 // CBN official rate - realistic XOF rate
};

export async function getOfficialRates(): Promise<CBNRate[]> {
  try {
    // Check cache first
    const cached = rateCache.get('official-rates');
    if (cached) {
      return cached;
    }

    // In production, this would fetch from Central Bank API
    // For now, we'll use realistic official rates updated daily that are consistently lower than parallel rates
    const rates = Object.entries(OFFICIAL_BASE_RATES).map(([currency, baseRate]) => {
      // Add minimal daily variation (±0.1%) to simulate daily rate updates
      const variation = (Math.random() - 0.5) * 0.002; // ±0.1% max for daily rates
      const currentRate = baseRate * (1 + variation);
      
      return {
        currency,
        buyingRate: currentRate * 0.998, // Slightly lower buying rate
        sellingRate: currentRate * 1.002, // Slightly higher selling rate
        centralRate: currentRate,
        lastUpdated: new Date()
      };
    });

    // Cache for 24 hours (daily rate updates)
    rateCache.set('official-rates', rates, 86400000);
    return rates;
  } catch (error) {
    console.error('Error fetching official rates:', error);
    
    // Fallback to static rates
    return Object.entries(OFFICIAL_BASE_RATES).map(([currency, rate]) => ({
      currency,
      buyingRate: rate * 0.998,
      sellingRate: rate * 1.002,
      centralRate: rate,
      lastUpdated: new Date()
    }));
  }
}

export async function getOfficialRate(currency: string): Promise<number> {
  const rates = await getOfficialRates();
  const rate = rates.find(r => r.currency === currency);
  return rate?.centralRate || 0;
}